import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import FlexTable from 'components/FlexTable';
import CardIcon from 'components/CardIcon';
import moment from 'moment';
import __ from 'utils/i18n';
import '../../../styles.scss';

const getChargesColumns = () => {
  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'succeeded': 'success',
      'pending': 'warning',
      'processing': 'warning',
      'failed': 'danger',
      'cancelled': 'secondary',
      'pastdue': 'warning',
      'skippedzeroamount': 'secondary',
      'skippednullamount': 'danger',
    };
    return statusClasses[status] || 'secondary';
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'succeeded': __('dashboard.sections.recurringPayments.charges.table.statuses.succeeded'),
      'pending': __('dashboard.sections.recurringPayments.charges.table.statuses.pending'),
      'processing': __('dashboard.sections.recurringPayments.charges.table.statuses.processing'),
      'failed': __('dashboard.sections.recurringPayments.charges.table.statuses.failed'),
      'cancelled': __('dashboard.sections.recurringPayments.charges.table.statuses.cancelled'),
      'pastdue': __('dashboard.sections.recurringPayments.charges.table.statuses.pastdue'),
      'skippedzeroamount': __('dashboard.sections.recurringPayments.charges.table.statuses.skippedzeroamount'),
      'skippednullamount': __('dashboard.sections.recurringPayments.charges.table.statuses.skippednullamount'),
    };
    return statusTexts[status] || status;
  };

  return [
    {
      Header: __('dashboard.sections.recurringPayments.charges.table.subscriber'),
      accessor: 'customerName',
      Cell: ({ original }) => (
        <div className="table-text-wrap-cell">
          <div className="font-weight-medium">
            {original.customerName || '-'}
            {original.externalReference && (
              <span className="text-muted ml-2" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                (<small style={{ fontSize: '0.775rem', fontWeight: 'normal' }}>{__('dashboard.sections.recurringPayments.subscribers.detail.idRef')}</small>: {original.externalReference})
              </span>
            )}
          </div>
          <small className="text-muted">{original.customerEmail || '-'}</small>
        </div>
      ),
      width: 280,
    },
    {
      Header: __('dashboard.sections.recurringPayments.charges.table.plan'),
      accessor: 'planName',
      Cell: ({ original }) => (
        <span className="table-text-wrap-cell">{original.planName || '-'}</span>
      ),
      width: 180,
    },
    {
      Header: __('dashboard.sections.recurringPayments.charges.table.paymentMethod'),
      accessor: 'paymentMethodLast4',
      Cell: ({ original }) => {
        const last4 = original.paymentMethodLast4;
        const brand = original.paymentMethodBrand;
        if (!last4) return '-';
        return (
          <div className="d-flex align-items-center">
            <CardIcon src={{ issuerName: brand }} />
            <span className="ml-2">**** {last4}</span>
          </div>
        );
      },
      width: 180,
    },
    {
      Header: __('dashboard.sections.recurringPayments.charges.table.date'),
      accessor: 'createdAt',
      Cell: ({ original }) => {
        if (!original.createdAt) return '-';
        return moment(original.createdAt).format('DD/MM/YYYY HH:mm');
      },
      width: 150,
    },
    {
      Header: __('dashboard.sections.recurringPayments.charges.table.amount'),
      accessor: 'amount',
      Cell: ({ original }) => (
        <span className="text-wrap-amount">{formatCurrency(original.amount, original.currency)}</span>
      ),
      width: 130,
    },
    {
      Header: __('dashboard.sections.recurringPayments.charges.table.status'),
      accessor: 'status',
      Cell: ({ original, index }) => {
        const badgeClass = getStatusBadgeClass(original.status);
        const statusText = getStatusText(original.status);
        const isRejected = original.status === 'failed' || original.status === 'cancelled';
        const isSkipped = original.status === 'skippedzeroamount' || original.status === 'skippednullamount';
        const tooltipId = `charge-status-tooltip-${original.chargeId || index}`;
        
        return (
          <span className={`badge badge-${badgeClass}`}>
            {statusText}
            {(isRejected || isSkipped) && (
              <>
                <i 
                  id={tooltipId}
                  className="mdi mdi-information-outline ml-1"
                  style={{ fontSize: '12px', cursor: 'pointer' }}
                ></i>
                <UncontrolledTooltip placement="top" target={tooltipId}>
                  {isSkipped 
                    ? __(`dashboard.sections.recurringPayments.charges.table.tooltips.${original.status}`)
                    : original.failureReason
                  }
                </UncontrolledTooltip>
              </>
            )}
          </span>
        );
      },
      width: 150,
    },
  ];
};

export const ChargesTable = ({ charges, onRowClick }) => {
  const columns = getChargesColumns();

  const handleDetails = (data) => {
    onRowClick && onRowClick(data);
  };

  return (
    <div className="table-container">
      {charges && charges.length > 0 && (
        <FlexTable
          data={charges}
          columns={columns}
          onRowClick={({ index }) => handleDetails(charges[index])}
        />
      )}
    </div>
  );
};

